import { useState } from 'react';
import { useApp } from '../context/AppContext';

interface AdminAccessModalProps {
  onClose: () => void;
}

export default function AdminAccessModal({ onClose }: AdminAccessModalProps) {
  const { setAdminBypass, setUserRole, setAccessCode } = useApp();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/validate-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data = await response.json();

      if (data.valid && data.type === 'admin') {
        setAccessCode(code.trim());
        setUserRole('admin');
        setAdminBypass(true);
        onClose();
      } else {
        setError('Invalid admin code. Please try again.');
      }
    } catch {
      setError('Unable to verify code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop">
      <div 
        className="absolute inset-0"
        onClick={onClose}
      />
      <div className="relative bg-autumn-cream rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-autumn-light-brown hover:text-autumn-brown transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-autumn-orange/10 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-autumn-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-display font-semibold text-autumn-brown">
            Admin Access
          </h2>
          <p className="text-autumn-light-brown mt-2 font-body">
            Enter the admin code to preview the site before the wedding
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Enter admin code"
              className="input-autumn text-center text-xl tracking-widest uppercase"
              autoFocus
              maxLength={20}
            />
          </div>

          {error && (
            <p className="text-autumn-red text-sm text-center font-body">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading || !code.trim()}
            className="btn-autumn w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </span>
            ) : (
              'Access Site'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

